import { GAME_WIDTH, GAME_HEIGHT, SHIELD } from '../config/gameConfig.js';

export class GameUI {
  /** @param {Phaser.Scene} scene */
  constructor(scene) {
    this.scene = scene;

    this.hudBg = scene.add.graphics().setDepth(298);
    const hx = 6;
    const hy = 6;
    const hw = 248;
    const hh = 88;
    this.hudBg.fillStyle(0x100205, 0.55);
    this.hudBg.fillRoundedRect(hx, hy, hw, hh, 12);
    this.hudBg.lineStyle(1.5, 0x8a6070, 0.72);
    this.hudBg.strokeRoundedRect(hx, hy, hw, hh, 12);

    const mk = (x, y, size = 15) =>
      scene.add.text(x, y, '', {
        fontFamily: 'monospace',
        fontSize: `${size}px`,
        color: '#ffd5dc',
      });

    this.scoreText = mk(14, 12, 16);
    this.sizeText = mk(14, 34, 14);
    this.powerText = mk(14, 56, 13);
    this.scoreText.setDepth(300);
    this.sizeText.setDepth(300);
    this.powerText.setDepth(300);

    this.fpsText = mk(GAME_WIDTH - 110, 10, 12);
    this.fpsText.setDepth(300);
    this.fpsText.setVisible(false);

    this.hitGfx = scene.add.graphics();
    this.hitGfx.setDepth(299);
    this.hitGfx.setVisible(false);

    this.flashRect = scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH + 8, GAME_HEIGHT + 8, 0xffffff, 0)
      .setDepth(420)
      .setScrollFactor(0);

    this._slowMoFxBuilt = false;
    /** @type {Phaser.GameObjects.Rectangle | null} */
    this.slowMoWash = null;
    /** @type {Phaser.GameObjects.Graphics | null} */
    this.slowMoVignette = null;
    this.slowMoFxActive = false;
  }

  ensureSlowMoFxLayers() {
    if (this._slowMoFxBuilt) return;
    this._slowMoFxBuilt = true;

    this.slowMoWash = this.scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH + 12, GAME_HEIGHT + 12, 0x221028, 0)
      .setDepth(261)
      .setScrollFactor(0)
      .setAlpha(0)
      .setVisible(false);

    const g = this.scene.add.graphics({ x: 0, y: 0 }).setDepth(262).setScrollFactor(0);
    g.fillStyle(0x060206, 0.72);
    g.fillRect(0, 0, GAME_WIDTH, 76);
    g.fillRect(0, GAME_HEIGHT - 76, GAME_WIDTH, 76);
    g.fillStyle(0x060206, 0.58);
    g.fillRect(0, 0, 92, GAME_HEIGHT);
    g.fillRect(GAME_WIDTH - 92, 0, 92, GAME_HEIGHT);
    g.setVisible(false);
    this.slowMoVignette = g;
  }

  /**
   * Subtle vignette + wash for slow-motion (paired with scene timeScale).
   * @param {boolean} active
   */
  setSlowMoFx(active) {
    this.ensureSlowMoFxLayers();
    if (this.slowMoFxActive === active) return;
    this.slowMoFxActive = active;
    if (!this.slowMoWash || !this.slowMoVignette) return;
    this.slowMoWash.setVisible(active);
    this.slowMoVignette.setVisible(active);
    this.scene.tweens.killTweensOf([this.slowMoWash, this.slowMoVignette]);
    if (active) {
      this.slowMoWash.setAlpha(0);
      this.scene.tweens.add({
        targets: this.slowMoWash,
        alpha: 0.18,
        duration: 180,
        ease: 'Sine.out',
      });
    } else {
      this.scene.tweens.add({
        targets: this.slowMoWash,
        alpha: 0,
        duration: 220,
        ease: 'Sine.in',
        onComplete: () => {
          if (!this.slowMoFxActive && this.slowMoWash) this.slowMoWash.setVisible(false);
        },
      });
    }
  }

  /** Quick fullscreen white punch */
  flashWhite(durationMs = 78, peakAlpha = 0.78) {
    this.flashRect.setAlpha(0);
    this.scene.tweens.killTweensOf(this.flashRect);
    this.flashRect.setAlpha(peakAlpha);
    this.scene.tweens.add({
      targets: this.flashRect,
      alpha: 0,
      duration: durationMs,
      ease: 'Cubic.out',
    });
  }

  updateHud(
    score,
    radius,
    shieldStages,
    shieldMax,
    cloneCooldown,
    cloneUnlocked,
    speedT,
    invisT,
    slowT
  ) {
    const safeScore = Number.isFinite(score) ? score : 0;
    const safeR = Number.isFinite(radius) ? radius : 0;
    const stages = Number.isFinite(shieldStages) ? Math.max(0, shieldStages) : 0;
    const smax = Number.isFinite(shieldMax) ? shieldMax : SHIELD.maxStages;

    this.scoreText.setText(`Score ${Math.floor(safeScore)}`);
    this.sizeText.setText(`Mass ${safeR.toFixed(0)}`);

    const parts = [];
    parts.push(`Shield ${stages}/${smax}`);
    if (cloneUnlocked) {
      if (Number.isFinite(cloneCooldown) && cloneCooldown > 0) {
        parts.push(`Clone ${cloneCooldown.toFixed(0)}s`);
      } else {
        parts.push('Space — decoys');
      }
    }
    if (Number.isFinite(speedT) && speedT > 0) parts.push(`Speed ${speedT.toFixed(1)}s`);
    if (Number.isFinite(invisT) && invisT > 0) parts.push(`Stealth ${invisT.toFixed(1)}s`);
    if (Number.isFinite(slowT) && slowT > 0) parts.push(`Slow-mo ${slowT.toFixed(1)}s`);
    this.powerText.setText(parts.join(' | ') || '');
  }

  updateFps(show, fps) {
    this.fpsText.setVisible(show);
    if (show) this.fpsText.setText(`${(Number.isFinite(fps) ? fps : 0).toFixed(0)} FPS`);
  }

  /** @param {import('../entities/Player.js').Player} player */
  drawHitboxes(show, player, pools) {
    this.hitGfx.clear();
    if (!show) {
      this.hitGfx.setVisible(false);
      return;
    }
    this.hitGfx.setVisible(true);

    const strokeCircle = (gx, gy, r, color, alpha = 0.85) => {
      this.hitGfx.lineStyle(2, color, alpha);
      this.hitGfx.strokeCircle(gx, gy, r);
    };

    strokeCircle(player.x, player.y, player.radius, 0x66ffaa);

    for (const pool of pools.gamePools) {
      for (const img of pool.iterateActive()) {
        strokeCircle(img.x, img.y, img.poolData.radius ?? 8, 0xff6688);
      }
    }
  }
}
